/**
 * <s-gallery-dots> — mobile swipe gallery pager.
 * Marks the dot for the slide in view (aria-current) and jumps to a slide on the --go command.
 *
 * Required markup:
 *   [data-product-gallery]           — horizontal scroll-snap track; each direct child is one slide.
 *   button[data-gallery-dot][data-index] with commandfor=<this id> command="--go".
 */
class SGalleryDots extends HTMLElement {
  #ac;
  #io;
  #slides = [];
  #dots = [];

  connectedCallback() {
    const track = this.querySelector('[data-product-gallery]');
    this.#dots = [...this.querySelectorAll('[data-gallery-dot]')];
    if (!track || this.#dots.length === 0) return;

    this.#slides = [...track.children];
    this.#ac = new AbortController();

    // CommandEvent fires on the commandfor target (this element) and does not bubble.
    this.addEventListener(
      'command',
      (event) => {
        if (event.command !== '--go' || !(event.source instanceof HTMLElement)) return;
        const slide = this.#slides[Number(event.source.dataset.index)];
        if (!slide) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        slide.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      },
      { signal: this.#ac.signal }
    );

    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(this.#slides.indexOf(entry.target));
        }
      },
      { root: track, threshold: 0.6 }
    );
    this.#slides.forEach((slide) => this.#io.observe(slide));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#io?.disconnect();
  }

  #setActive(index) {
    if (index < 0) return;
    this.#dots.forEach((dot, n) => dot.setAttribute('aria-current', n === index ? 'true' : 'false'));
  }
}

if (!customElements.get('s-gallery-dots')) customElements.define('s-gallery-dots', SGalleryDots);
